import {
  KeyboardEventHandler,
  Ref,
  RefCallback,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  GetToggleButtonPropsOptions,
  useSelect,
  type UseSelectSelectedItemChange,
  type UseSelectState,
  type UseSelectStateChangeOptions,
} from 'downshift';

import { useForwardedRef, usePrevious } from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import {
  checkReferenceEquality,
  createDefaultGetItemKey,
  defaultLabelGetter,
  getAriaProps,
  getSingleSelectionHandlers,
  isGroupHeader,
  safeScrollIntoView,
  wrapGetItemProps,
} from '../helpers';
import type {
  DefaultItem,
  DropdownGetMenuProps,
  GenericItem,
  GetItemProps,
  GetToggleButtonProps,
  GroupHeader,
  InputItemGroup,
  UseDropdownProps,
} from '../types';
import { useGroupFilter } from './index';
import useDropdownId from './useDropdownId';
import useGroupedItems from './useGroupedItems';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import useVirtualProps from './useVirtualProps';

/**
 * Downshift escape hatch: setting `preventDownshiftDefault` to `true` on a keyboard event
 * prevents Downshift's own key handler from running, so the library's default state transition
 * (e.g. closing the menu on Enter) is bypassed while React's and the consumer's handlers still fire.
 */
type DownshiftKeyboardEscapeHatch<
  E extends React.KeyboardEvent<HTMLElement> =
    React.KeyboardEvent<HTMLButtonElement>,
> = E & { preventDownshiftDefault?: boolean };

const assignToggleRef = <T extends HTMLElement>(
  ref: Ref<T> | undefined | null,
  node: T | null,
) => {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    (ref as RefCallback<T>)(node);
  } else {
    ref.current = node;
  }
};

/**
 * A hook used to manage all logic needed for a Dropdown.
 */
const useDropdown = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLButtonElement,
>({
  id,
  itemToString = (item) => defaultLabelGetter(item as DefaultItem | null),
  items,
  groupedItems,
  target,
  overlayRef: overlayRefProp,
  layoutDependency,
  getMenuProps: getMenuPropsProp,
  stateReducer: stateReducerProp,
  getItemKey: getItemKeyProp,
  onItemSelect: onItemSelectProp,
  onSelectedItemChange: onSelectedItemChangeProp,
  ...props
}: UseDropdownProps<TItem, TTarget>) => {
  const getItemKey = useMemo(
    () => getItemKeyProp || createDefaultGetItemKey(itemToString),
    [getItemKeyProp, itemToString],
  );

  const dropdownId = useDropdownId(id);
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const ariaProps = getAriaProps(props);
  const {
    allowCollapsibleGroups,
    showGroupItemCount,
    allowGroupFiltering,
    showEmptyGroups,
  } = props;

  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string>('group0');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>();
  const lastKeyWasGroupHeaderRef = useRef(false);

  const {
    flatItems: flatGroupedItems,
    indexGetter,
    getMenuItems,
    getAllGroupHeaders,
  } = useGroupedItems({
    maybeGroupedItems,
    allowCollapsibleGroups,
    expandedGroupKey,
    selectedGroupKey,
    showEmptyGroups,
  });

  const { filteredGroupItems: flatItems, showGroupHeaders } = useGroupFilter({
    items: flatGroupedItems,
    maybeGroupedItems,
    selectedGroupKey,
    getItemKey,
  });

  const setSelectedGroupKeyWithExpand = useCallback(
    (key: string | undefined) => {
      setSelectedGroupKey(key);
      if (allowCollapsibleGroups) {
        setExpandedGroupKey(key !== undefined && key !== '' ? key : 'group0');
      }
    },
    [allowCollapsibleGroups],
  );

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const dropdownStateReducer = useCallback(
    (
      state: UseSelectState<TItem>,
      { changes: _changes, type }: UseSelectStateChangeOptions<TItem>,
    ): Partial<UseSelectState<TItem>> => {
      const changes = {
        ..._changes,
        highlightedIndex: _changes.isOpen ? _changes.highlightedIndex : -1,
      };
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonBlur:
          return {
            ...changes,
            isOpen: getShouldKeepMenuOpenOnBlur(),
          };
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
          if (
            _changes.selectedItem !== null &&
            isGroupHeader(_changes.selectedItem as unknown as GroupHeader)
          ) {
            lastKeyWasGroupHeaderRef.current = true;
            return {
              ...changes,
              selectedItem: state.selectedItem,
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
            };
          }
          return changes;
        case useSelect.stateChangeTypes.ItemClick:
          if (
            _changes.selectedItem !== null &&
            isGroupHeader(_changes.selectedItem as unknown as GroupHeader)
          ) {
            return {
              ...changes,
              selectedItem: state.selectedItem,
              isOpen: true,
              highlightedIndex: -1,
            };
          }
          return changes;
        default:
          return changes;
      }
    },
    [getShouldKeepMenuOpenOnBlur],
  );

  const stateReducer = useCallback(
    (
      state: UseSelectState<TItem>,
      actionAndChanges: UseSelectStateChangeOptions<TItem>,
    ) => {
      const updatedState = dropdownStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [dropdownStateReducer, stateReducerProp],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const { menuItems, selectableItems } = useMemo(() => {
    getMenuItems(flatGroupedItems);
    const menu = getMenuItems(flatItems, false) as MenuItems;
    const selectable = (menu as (GroupHeader | TItem)[]).filter(
      (item): item is TItem => !isGroupHeader(item),
    );
    return { menuItems: menu, selectableItems: selectable };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatItems, flatGroupedItems, getMenuItems, expandedGroupKey]);

  const hasGroupHeadersInMenu = menuItems.length > selectableItems.length;
  const downshiftItems =
    allowCollapsibleGroups || hasGroupHeadersInMenu
      ? menuItems
      : selectableItems;
  const effectiveItemToString = useCallback(
    (item: TItem | GroupHeader | null) => {
      if (item === null) return '';
      if (isGroupHeader(item)) return (item as GroupHeader).label ?? '';
      return itemToString(item as TItem);
    },
    [itemToString],
  );

  const onSelectedItemChange = useCallback(
    (changes: UseSelectSelectedItemChange<TItem>) => {
      onSelectedItemChangeProp?.(changes);

      const { type, selectedItem: item } = changes;
      if (lastKeyWasGroupHeaderRef.current) {
        lastKeyWasGroupHeaderRef.current = false;
        return;
      }
      if (
        onItemSelectProp &&
        item &&
        (type === useSelect.stateChangeTypes.ItemClick ||
          type === useSelect.stateChangeTypes.ToggleButtonKeyDownEnter ||
          type === useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton)
      ) {
        onItemSelectProp(item, {
          action: 'select',
          selectedItem: item,
          selectedItems: [item],
        });
      }
    },
    [onItemSelectProp, onSelectedItemChangeProp],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getItemProps: rawGetItemProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    getToggleButtonProps: rawGetToggleButtonProps,
    openMenu,
    highlightedIndex,
    selectedItem,
    ...useSelectReturnValue
  } = useSelect({
    id: dropdownId,
    itemToString:
      allowCollapsibleGroups || hasGroupHeadersInMenu
        ? effectiveItemToString
        : itemToString,
    items: downshiftItems as TItem[],
    stateReducer,
    scrollIntoView: safeScrollIntoView,
    ...props,
    onSelectedItemChange,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    onHide: useSelectReturnValue.closeMenu,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const getMenuProps: DropdownGetMenuProps = useCallback(
    (options, otherOptions) => {
      return getMenuPropsProp
        ? rawGetMenuProps(
            getMenuPropsProp({ ...overlayProps, ...options }, otherOptions),
            otherOptions,
          )
        : rawGetMenuProps({ ...overlayProps, ...options }, otherOptions);
    },
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  ) as DropdownGetMenuProps;

  const allGroupHeadersMenuItems = useMemo(() => {
    if (!getAllGroupHeaders || getAllGroupHeaders.length === 0) {
      return menuItems;
    }
    return [...getAllGroupHeaders] as unknown as MenuItems;
  }, [getAllGroupHeaders, menuItems]);

  const defaultIndexGetter = useCallback(
    (item: TItem, _indexHint?: number) =>
      allowCollapsibleGroups
        ? (menuItems as (GroupHeader | TItem)[]).indexOf(item)
        : selectableItems.indexOf(item),
    [allowCollapsibleGroups, menuItems, selectableItems],
  );

  const getItemProps: GetItemProps<TItem> = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        highlightedIndex,
        selectedItem,
        indexGetter: allowCollapsibleGroups
          ? null
          : indexGetter || defaultIndexGetter,
        getItemKey,
      })(args),
    [
      allowCollapsibleGroups,
      defaultIndexGetter,
      getItemKey,
      highlightedIndex,
      indexGetter,
      rawGetItemProps,
      selectedItem,
    ],
  );

  const toggleRefAssignmentsRef = useRef<{
    downshiftRef?: Ref<HTMLButtonElement>;
    refProp?: Ref<HTMLButtonElement>;
    targetPropRef?: Ref<HTMLButtonElement>;
    targetRef?: Ref<HTMLButtonElement>;
  }>({});

  const toggleNodeRef = useRef<HTMLButtonElement | null>(null);

  /**
   * One-shot latch armed when the Dropdown is open at mount (via `initialIsOpen`, `defaultIsOpen`,
   * or a controlled `isOpen` prop). It intercepts the first keyboard-triggered Enter or
   * keyboard-synthesised click on the toggle button so that a host element's Enter keydown that
   * caused this Dropdown to mount already-open does not immediately close the menu.
   *
   * Cleared by the first real pointer interaction (mousedown on the button) or after the first
   * keyboard-triggered event is handled — whichever comes first.
   *
   * The `useLayoutEffect` below attaches a capture listener on the toggle node for the click path
   * (browser synthesises a `detail === 0` click when focus transfers to a button while Enter is
   * held). The `onKeyDown` guard below handles the path where the button directly receives the
   * keydown; both paths must call `preventDefault()` to suppress native button activation.
   *
   * `useKeepMenuOpenOnBlur`'s handler only reacts to Tab, so the early return in the keydown guard
   * skips `controlOnKeyDown` only after explicitly forwarding the event to it.
   */
  const blockInitialEnterRef = useRef(
    !!(props.initialIsOpen || props.defaultIsOpen || props.isOpen),
  );
  const removeCaptureListenersRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (!blockInitialEnterRef.current) return undefined;
    const node = toggleNodeRef.current;
    if (!node) return undefined;

    const handlers = {
      click: (e: MouseEvent) => {
        if (e.detail === 0) {
          e.stopImmediatePropagation();
          e.preventDefault();
        }
        blockInitialEnterRef.current = false;
        removeCaptureListenersRef.current?.();
      },
      mousedown: () => {
        blockInitialEnterRef.current = false;
        removeCaptureListenersRef.current?.();
      },
    };

    removeCaptureListenersRef.current = () => {
      node.removeEventListener('click', handlers.click, true);
      node.removeEventListener('mousedown', handlers.mousedown, true);
      removeCaptureListenersRef.current = null;
    };
    node.addEventListener('click', handlers.click, true);
    node.addEventListener('mousedown', handlers.mousedown, true);
    return () => {
      removeCaptureListenersRef.current?.();
    };
  }, []);

  /**
   * Single-select fans the toggle node into Downshift, overlay `target`, and optional
   * consumer refs from `getToggleButtonProps`. Multi-select merges refs via
   * `getDropdownProps` instead; keep both paths aligned when changing ref wiring.
   */
  const mergeToggleRef = useCallback((node: HTMLButtonElement | null) => {
    toggleNodeRef.current = node;
  }, []);

  useLayoutEffect(() => {
    const node = toggleNodeRef.current;
    const assignments = toggleRefAssignmentsRef.current;
    assignToggleRef(assignments.downshiftRef, node);
    assignToggleRef(assignments.refProp, node);
    assignToggleRef(assignments.targetPropRef, node);
    assignToggleRef(assignments.targetRef, node);
  });

  const getToggleButtonProps = useCallback(
    ({
      onKeyDown: onKeyDownProp,
      ref: refProp,
      ...options
    }: GetToggleButtonPropsOptions = {}) => {
      const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          blockInitialEnterRef.current = false;
          removeCaptureListenersRef.current?.();
        }

        if (blockInitialEnterRef.current && e.key === 'Enter') {
          e.preventDefault();
          blockInitialEnterRef.current = false;
          removeCaptureListenersRef.current?.();
          (
            e as DownshiftKeyboardEscapeHatch<typeof e>
          ).preventDownshiftDefault = true;
          controlOnKeyDown(e);
          onKeyDownProp?.(e);
          return;
        }

        if (
          allowCollapsibleGroups &&
          (e.key === 'Enter' || e.key === ' ') &&
          highlightedIndex >= 0 &&
          highlightedIndex < menuItems.length
        ) {
          const highlighted = menuItems[highlightedIndex];
          if (highlighted && isGroupHeader(highlighted)) {
            e.preventDefault();
            // downshift's callAllEventHandlers only stops on preventDownshiftDefault, not defaultPrevented
            (
              e as unknown as { preventDownshiftDefault: boolean }
            ).preventDownshiftDefault = true;
            const groupKey = (highlighted as GroupHeader).groupKey as string;
            setExpandedGroupKey((prev) => (prev === groupKey ? '' : groupKey));
            onKeyDownProp?.(e);
            return;
          }
        }
        controlOnKeyDown(e);
        onKeyDownProp?.(e);
      };

      const toggleButtonProps = rawGetToggleButtonProps({
        ...options,
        ref: refProp,
        onKeyDown,
      });

      const {
        'aria-controls': ariaControls,
        ref: downshiftRef,
        ...restProps
      } = toggleButtonProps;

      toggleRefAssignmentsRef.current = {
        downshiftRef,
        refProp,
        targetPropRef: targetProps.ref as Ref<HTMLButtonElement>,
        targetRef: targetRef as unknown as Ref<HTMLButtonElement>,
      };

      return {
        ...restProps,
        ref: mergeToggleRef as unknown as Ref<HTMLButtonElement>,
        ...(useOverlayIsOpen && ariaControls
          ? { 'aria-controls': ariaControls }
          : {}),
      };
    },
    [
      allowCollapsibleGroups,
      controlOnKeyDown,
      highlightedIndex,
      menuItems,
      mergeToggleRef,
      rawGetToggleButtonProps,
      setExpandedGroupKey,
      targetProps.ref,
      targetRef,
      useOverlayIsOpen,
    ],
  ) as GetToggleButtonProps;

  // When expanded group changes (e.g. after arrowing to another group header or toggling a
  // group via Enter/Space), move highlight to the first selectable item so Enter selects it.
  const prevExpandedGroupKey = usePrevious(expandedGroupKey);
  useLayoutEffect(() => {
    if (
      allowCollapsibleGroups &&
      expandedGroupKey &&
      prevExpandedGroupKey !== undefined &&
      expandedGroupKey !== prevExpandedGroupKey &&
      menuItems.length > 0 &&
      downshiftIsMenuOpen
    ) {
      const firstItemIndex = (menuItems as (GroupHeader | TItem)[]).findIndex(
        (item) => !isGroupHeader(item),
      );
      if (firstItemIndex >= 0) {
        useSelectReturnValue.setHighlightedIndex(firstItemIndex);
      }
    }
  }, [
    allowCollapsibleGroups,
    downshiftIsMenuOpen,
    expandedGroupKey,
    menuItems,
    prevExpandedGroupKey,
    useSelectReturnValue,
  ]);

  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader,
    showEmptyGroups,
    showGroupHeaders,
  });

  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useSelectReturnValue.closeMenu,
    refToFocusOnClear: targetRef,
    selectedItem,
  });

  return {
    controlProps: {
      ref: targetRef,
      isOpen: useOverlayIsOpen,
      isAutocomplete: false as const,
      getToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
      'data-dropdown-control-container': 'true',
      ...(props['data-testid'] ? { 'data-testid': props['data-testid'] } : {}),
      ...ariaProps,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: false as const,
      menuItems,
      allGroupHeadersMenuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      getItemKey,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
      allowCollapsibleGroups,
      allowGroupFiltering,
      showGroupItemCount,
      groupedItems: maybeGroupedItems,
      setExpandedGroupKey,
      expandedGroupKey,
      selectedGroupKey,
      setSelectedGroupKey: setSelectedGroupKeyWithExpand,
      showGroupHeaders,
      setHighlightedIndex: useSelectReturnValue.setHighlightedIndex,
      refToFocusOnClick: targetRef,
      showEmptyGroups,
      dropdownId,
      ...singleSelectionHandlers.menuProps,
    },
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem,
    ...useSelectReturnValue,
  };
};

export default Object.assign(useDropdown, {
  stateChangeTypes: useSelect.stateChangeTypes,
});
